import discountQueries from '@apis/queries/discountQueries';
import usePrepaidCardFetchEnabled from '@hooks/prepaidCard/usePrepaidCardFetchEnabled';
import { DiscountPrepaidCardRow } from '@models/discount';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

/** 선불카드 선택 상태와 적용. 한 장만 고를 수 있다 */
function useOrderPrepaidCards() {
    const { pop } = useFlow();
    const dispatch = useDispatch();
    const state = useCalculatedRequest();
    const { shopId, prepaidCardPayments } = state;
    const { data: discountResponse } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const prepaidCardsFetchEnabled = usePrepaidCardFetchEnabled();
    const { data, status } = useQuery({
        ...discountQueries.prepaidCards(shopId),
        enabled: !!shopId && prepaidCardsFetchEnabled,
    });
    const isFetching = status === 'pending';
    const cards = data?.prepaidCards;

    const [selectedCardId, setSelectedCardId] = useState<string | null>(
        () => prepaidCardPayments?.[0]?.userPrepaidCardId ?? null,
    );
    const selectedCard = cards?.find((card) => card.id === selectedCardId);

    // 선불카드를 빼고 남은 PG 결제 금액 안에서 잔액만큼 사용한다
    const getUseAmount = (card: DiscountPrepaidCardRow) => {
        const { pgAmount } = orderWithTotalAmount({
            ...state,
            prepaidCardPayments: [],
        }, discountResponse);
        return Math.min(pgAmount, card.balance);
    };
    const draftAmount = selectedCard ? getUseAmount(selectedCard) : 0;

    const togglePrepaidCard = (cardId: string) => {
        setSelectedCardId((prev) => (prev === cardId ? null : cardId));
    };

    const handleApply = () => {
        dispatch(orderActions.setPrepaidCardPayments(selectedCard ? [
            { userPrepaidCardId: selectedCard.id, amount: getUseAmount(selectedCard) },
        ] : []));
        pop();
    };

    return {
        cards,
        selectedCardId,
        draftAmount,
        // 선택도 없고 적용 중인 카드도 없으면 적용할 것이 없다
        applyDisabled: !selectedCard && !prepaidCardPayments?.length,
        togglePrepaidCard,
        handleApply,
        handleClose: pop,
        isFetching,
    };
}

export default useOrderPrepaidCards;
